import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { cx } from "@/components/ui";

/**
 * A button that opens a panel under it. Click outside or Esc closes it and returns focus to the button.
 * `trigger` gets the open state and the props to spread on the trigger button.
 */
export function Popover({
  trigger,
  children,
  align = "left",
  side = "bottom",
  width = 260,
  className,
}: {
  trigger: (p: { open: boolean; toggle: () => void; props: Record<string, unknown> }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: "left" | "right";
  /** Opens above the trigger, e.g. from a bar at the bottom of the screen. */
  side?: "top" | "bottom";
  width?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        ref.current?.querySelector<HTMLElement>("[aria-haspopup]")?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    // Focus the first input (a search box) or item.
    requestAnimationFrame(() =>
      ref.current?.querySelector<HTMLElement>(`#${CSS.escape(id)} input, #${CSS.escape(id)} button`)?.focus(),
    );
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, id]);

  const close = () => setOpen(false);
  return (
    <div ref={ref} className={cx("relative inline-flex", className)}>
      {trigger({
        open,
        toggle: () => setOpen((o) => !o),
        props: { "aria-haspopup": "dialog", "aria-expanded": open, "aria-controls": open ? id : undefined },
      })}
      {open && (
        <div
          id={id}
          role="dialog"
          style={{ width }}
          className={cx(
            "absolute z-40 flex max-h-[min(420px,70vh)] flex-col overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface shadow-[var(--shadow)]",
            side === "top" ? "bottom-full mb-1" : "top-full mt-1",
            align === "right" ? "right-0" : "left-0",
          )}
        >
          {children(close)}
        </div>
      )}
    </div>
  );
}

/** One row of a picker list. */
export function PickRow({
  selected,
  onClick,
  children,
  multi,
  disabled,
  title,
}: {
  selected?: boolean;
  onClick: () => void;
  children: ReactNode;
  multi?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      role={multi ? "menuitemcheckbox" : "menuitemradio"}
      aria-checked={!!selected}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cx(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-surface-2 focus-visible:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent",
        selected && "font-semibold",
      )}
    >
      {multi && (
        <span
          aria-hidden="true"
          className={cx(
            "inline-flex size-4 shrink-0 items-center justify-center rounded border",
            selected ? "border-accent bg-accent text-accent-fg" : "border-border-strong",
          )}
        >
          {selected && (
            <svg viewBox="0 0 16 16" width={11} height={11} fill="none" stroke="currentColor" strokeWidth={2.6}>
              <path d="M3 8.5l3 3 7-7" />
            </svg>
          )}
        </span>
      )}
      {children}
    </button>
  );
}

/** A search box at the top of a picker. */
export function PickSearch({
  value,
  onChange,
  placeholder,
  onEnter,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onEnter?: () => void;
}) {
  return (
    <div className="border-b border-border p-1.5">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          // Never submit a form the picker happens to sit in.
          if (e.key === "Enter") {
            e.preventDefault();
            onEnter?.();
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-8 w-full rounded-md border border-border-strong bg-surface px-2 text-sm text-text placeholder:text-muted"
      />
    </div>
  );
}
